import React, { useState, useRef, useEffect } from 'react';
import { useAppearance } from '../contexts/AppearanceContext';

const BetaTag = ({ title, description, size = 'sm', showTooltip = true }) => {
  const { isSuppleMode } = useAppearance();
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState('bottom');
  const tagRef = useRef(null);
  const tooltipRef = useRef(null);

  // Calculate optimal tooltip position based on viewport boundaries
  const calculateTooltipPosition = () => {
    if (!tagRef.current || !tooltipRef.current) return;

    const tagRect = tagRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let position = 'bottom'; // default

    // Check if tooltip would overflow viewport boundaries
    const spaceAbove = tagRect.top;
    const spaceBelow = viewport.height - tagRect.bottom;
    const spaceLeft = tagRect.left;
    const spaceRight = viewport.width - tagRect.right;

    // Prioritize positions based on available space
    if (spaceBelow >= tooltipRect.height + 10) {
      position = 'bottom';
    } else if (spaceAbove >= tooltipRect.height + 10) {
      position = 'top';
    } else if (spaceRight >= tooltipRect.width + 10) {
      position = 'right';
    } else if (spaceLeft >= tooltipRect.width + 10) {
      position = 'left';
    } else {
      // If no space, use the side with most available space
      const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight);
      if (maxSpace === spaceBelow) position = 'bottom';
      else if (maxSpace === spaceAbove) position = 'top';
      else if (maxSpace === spaceRight) position = 'right';
      else position = 'left';
    }

    setTooltipPosition(position);
  };

  // Recalculate position when tooltip becomes visible
  useEffect(() => {
    if (showTooltipState) {
      setTimeout(calculateTooltipPosition, 10);
    }
  }, [showTooltipState]);

  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1'
  };

  const getTooltipClasses = () => {
    const baseClasses = 'absolute z-[9999] px-3 py-2 text-sm rounded-lg shadow-lg whitespace-normal max-w-xs';
    
    switch (tooltipPosition) {
      case 'top':
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2 mb-2`;
      case 'bottom':
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 mt-2`;
      case 'left':
        return `${baseClasses} right-full top-1/2 transform -translate-y-1/2 mr-2`;
      case 'right':
        return `${baseClasses} left-full top-1/2 transform -translate-y-1/2 ml-2`;
      default:
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2 mt-2`;
    }
  };

  const getArrowClasses = () => {
    const baseClasses = 'absolute w-2 h-2 transform rotate-45';
    
    switch (tooltipPosition) {
      case 'top':
        return `${baseClasses} top-full left-1/2 -translate-x-1/2 -mt-1`;
      case 'bottom':
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 -mb-1`;
      case 'left':
        return `${baseClasses} left-full top-1/2 -translate-y-1/2 -ml-1`;
      case 'right':
        return `${baseClasses} right-full top-1/2 -translate-y-1/2 -mr-1`;
      default:
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 -mb-1`;
    }
  };

  return (
    <div className="relative" ref={tagRef}>
      <span 
        className={`inline-flex items-center font-medium rounded-full ${sizeClasses[size]} ${
          isSuppleMode 
            ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' 
            : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
        } shadow-sm cursor-help`}
        onMouseEnter={() => showTooltip && setShowTooltipState(true)}
        onMouseLeave={() => setShowTooltipState(false)}
      >
        BETA
      </span>
      
      {/* Tooltip */}
      {showTooltip && showTooltipState && title && (
        <div 
          ref={tooltipRef}
          className={`${getTooltipClasses()} ${
            isSuppleMode 
              ? 'supple-card-glass backdrop-blur-lg supple-border-subtle' 
              : 'bg-gray-900 text-white border border-gray-700'
          }`}
        >
          <div className={`font-semibold ${isSuppleMode ? 'supple-text-primary' : 'text-white'}`}>
            {title}
          </div>
          {description && (
            <div className={`text-xs mt-1 whitespace-normal ${isSuppleMode ? 'supple-text-muted' : 'text-gray-200'}`}>
              {description}
            </div>
          )}
          {/* Arrow */}
          <div className={`${getArrowClasses()} ${
            isSuppleMode ? 'supple-card-glass' : 'bg-gray-900'
          }`} />
        </div>
      )}
    </div>
  );
};

export default BetaTag;